import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Lock, Sparkles, Clock } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';

// Payment links come from frontend env only — no keys/URLs hardcoded here.
// If unset (empty), the button falls back to /signup so the page still works.
const STRIPE_PRO_URL = process.env.REACT_APP_STRIPE_PRO_URL || '';
const STRIPE_ENTERPRISE_URL = process.env.REACT_APP_STRIPE_ENTERPRISE_URL || '';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    priceSuffix: 'AUD',
    cadence: '',
    tagline: 'Build and preview your idea.',
    features: [
      { label: 'Build pages with AI' },
      { label: 'Live preview of your build' },
      { label: 'Publish and share (upgrade to publish)', gated: true },
      { label: '3 saved projects' },
      { label: '3 builds per day' },
      { label: 'Community support' },
    ],
    cta: { label: 'Start building free', href: '/signup', kind: 'secondary' },
    highlight: false,
  },
  {
    id: 'pro',
    name: 'Pro Builder',
    price: '$14.99',
    priceSuffix: 'AUD',
    cadence: '/month',
    tagline: 'Publish and grow.',
    features: [
      { label: 'Everything in Free' },
      { label: 'Publish and share live pages' },
      { label: 'Unlimited saved projects' },
      { label: '25 builds per day' },
      { label: 'Custom domain — coming soon', comingSoon: true },
      { label: 'Remove the "Built with YouBuild" badge' },
      { label: 'Priority email support' },
    ],
    cta: {
      label: 'Upgrade to Pro',
      envVar: 'REACT_APP_STRIPE_PRO_URL',
      stripeUrl: STRIPE_PRO_URL,
      kind: 'primary',
    },
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise Studio',
    price: '$39.99',
    priceSuffix: 'AUD',
    cadence: '/month',
    tagline: 'For serious builders.',
    features: [
      { label: 'Everything in Pro' },
      { label: '100 builds per day' },
      { label: 'Custom domains — coming soon', comingSoon: true },
      { label: 'Onboarding call + priority support' },
    ],
    cta: {
      label: 'Go Enterprise',
      envVar: 'REACT_APP_STRIPE_ENTERPRISE_URL',
      stripeUrl: STRIPE_ENTERPRISE_URL,
      kind: 'secondary',
    },
    highlight: false,
  },
];

/**
 * Build the Stripe Payment Link with client_reference_id + prefilled_email so
 * the webhook can map the paid checkout back to the YouBuild user.
 */
const buildCheckoutUrl = (baseUrl, user) => {
  if (!baseUrl) return '';
  try {
    const u = new URL(baseUrl);
    if (user?.id) u.searchParams.set('client_reference_id', user.id);
    if (user?.email) u.searchParams.set('prefilled_email', user.email);
    return u.toString();
  } catch {
    return baseUrl;
  }
};

const FeatureRow = ({ label, gated, comingSoon }) => {
  if (comingSoon) {
    return (
      <li
        className="flex items-start gap-2.5"
        style={{ color: 'var(--yb-muted)' }}
        data-testid="pricing-feature-coming-soon"
      >
        {/* No checkmark — a muted clock circle makes it clearly not-yet-available. */}
        <span
          className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ backgroundColor: '#F3F4F6', color: 'var(--yb-muted)' }}
          aria-hidden="true"
        >
          <Clock size={11} strokeWidth={2.5} />
        </span>
        <span className="text-[14.5px] leading-relaxed italic flex flex-wrap items-center gap-2">
          <span>{label}</span>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider not-italic"
            style={{ backgroundColor: '#F3F4F6', color: 'var(--yb-muted)' }}
          >
            Coming soon
          </span>
        </span>
      </li>
    );
  }
  return (
    <li
      className="flex items-start gap-2.5"
      style={{ color: gated ? 'var(--yb-muted)' : 'var(--yb-text)' }}
    >
      <span
        className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
        style={{
          backgroundColor: gated ? '#F3F4F6' : '#EEF2FF',
          color: gated ? 'var(--yb-muted)' : 'var(--yb-indigo)',
        }}
        aria-hidden="true"
      >
        {gated ? <Lock size={11} strokeWidth={2.5} /> : <Check size={12} strokeWidth={3} />}
      </span>
      <span className={`text-[14.5px] leading-relaxed ${gated ? 'italic' : ''}`}>
        {label}
      </span>
    </li>
  );
};

const PlanCTA = ({ plan }) => {
  const { cta } = plan;
  const { user } = useAuth();
  const navigate = useNavigate();
  const baseCls =
    'w-full mt-8 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-[14px] font-semibold transition-colors';
  const cls =
    cta.kind === 'primary'
      ? `${baseCls} yb-btn-primary`
      : `${baseCls} yb-btn-secondary`;

  // Free plan → SPA link to /signup (unchanged).
  if (cta.href) {
    return (
      <Link to={cta.href} data-testid={`pricing-cta-${plan.id}`} className={cls}>
        {cta.label}
      </Link>
    );
  }

  // Paid plans.
  const handleClick = (e) => {
    if (!cta.stripeUrl) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Pricing] ${cta.envVar} is not set — falling back to /signup. Set the Stripe Payment Link URL in the frontend env to enable checkout.`
      );
      return; // anchor href handles the fallback nav
    }
    if (!user) {
      // Not logged in — send them to /signup with a return hint.
      e.preventDefault();
      navigate('/signup?next=/#pricing');
      return;
    }
    // Logged-in + real Stripe URL: the anchor href already carries the
    // client_reference_id + prefilled_email, so let the browser open it.
  };

  const checkoutUrl = user && cta.stripeUrl ? buildCheckoutUrl(cta.stripeUrl, user) : cta.stripeUrl;
  const href = checkoutUrl || '/signup';
  const openInNewTab = !!checkoutUrl;

  return (
    <a
      href={href}
      onClick={handleClick}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      data-testid={`pricing-cta-${plan.id}`}
      className={cls}
    >
      {cta.label}
    </a>
  );
};

const PlanCard = ({ plan }) => {
  const highlight = plan.highlight;
  return (
    <div
      data-testid={`pricing-card-${plan.id}`}
      className={`relative bg-white rounded-2xl border p-7 sm:p-8 flex flex-col ${
        highlight ? 'lg:-translate-y-3' : ''
      }`}
      style={{
        borderColor: highlight ? 'var(--yb-indigo)' : 'var(--yb-border)',
        borderWidth: highlight ? 2 : 1,
        boxShadow: highlight
          ? '0 20px 50px -22px rgba(79, 70, 229, 0.35)'
          : '0 8px 28px -20px rgba(23, 21, 59, 0.2)',
      }}
    >
      {highlight && (
        <span
          data-testid="pricing-most-popular-badge"
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest"
          style={{ backgroundColor: 'var(--yb-indigo)', color: '#fff' }}
        >
          <Sparkles size={11} strokeWidth={2.5} />
          Most popular
        </span>
      )}

      <h3
        className="font-serif text-[26px] sm:text-[28px] leading-tight tracking-tight"
        style={{ color: 'var(--yb-ink)' }}
      >
        {plan.name}
      </h3>
      <p className="mt-1 text-[14px] leading-relaxed" style={{ color: 'var(--yb-muted)' }}>
        {plan.tagline}
      </p>

      <div className="mt-6 flex items-baseline gap-1.5">
        <span
          className="font-serif text-[44px] sm:text-[48px] leading-none tracking-tight"
          style={{ color: 'var(--yb-ink)' }}
        >
          {plan.price}
        </span>
        <span className="text-[13px] font-medium" style={{ color: 'var(--yb-muted)' }}>
          {plan.priceSuffix}
        </span>
        {plan.cadence && (
          <span className="text-[14px]" style={{ color: 'var(--yb-muted)' }}>
            {plan.cadence}
          </span>
        )}
      </div>

      <ul className="mt-7 flex-1 space-y-3">
        {plan.features.map((f) => (
          <FeatureRow
            key={f.label}
            label={f.label}
            gated={!!f.gated}
            comingSoon={!!f.comingSoon}
          />
        ))}
      </ul>

      <PlanCTA plan={plan} />
    </div>
  );
};

const Pricing = () => {
  return (
    <section
      id="pricing"
      data-testid="section-pricing"
      className="py-24 lg:py-32"
      style={{ backgroundColor: 'var(--yb-bg)', scrollMarginTop: '5rem' }}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <p className="text-[13px] uppercase tracking-widest mb-4" style={{ color: 'var(--yb-muted)' }}>
            Pricing
          </p>
          <h2
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em]"
            style={{ color: 'var(--yb-ink)' }}
          >
            Simple plans that grow with you.
          </h2>
          <p className="mt-5 text-[16px] leading-relaxed" style={{ color: 'var(--yb-muted)' }}>
            Start free, publish when you’re ready. Prices in AUD, cancel any time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 lg:items-stretch">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
